import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { connect } from 'redux-bundler-react';
import { useFormContext } from 'react-hook-form';
import { Alert, Accordion } from '@trussworks/react-uswds';
import { mdiKeyboard, mdiMapSearchOutline, mdiDatabaseMarker } from '@mdi/js';
import L from 'leaflet';
import * as turf from '@turf/turf';

import TextInput from '@components/text-input/TextInput';
import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import MultiSelectInput from '@components/multi-select/multiSelectInput';
import YesNoInput from '@components/yes-no-input/YesNoInput';
import CreatableSelectInput from '@components/new-inputs/creatableSelectInput';
import ModuleButton from '@pages/Resources/components/_shared/ModuleButton';
import Map from '@components/map/map';
import ReadOnlyMap from '@components/map/readOnlyMap';
import statusModal from '@forms/components/modals/statusModal';

import {
  scrollToElement,
  formatCoordFlt,
  formatCoordStr,
  isValidORMNumber,
  mapCountryAlpha3toAlpha2,
} from '@utils/helpers';
import { latRegex, lngRegex } from '@utils/regex';
import { ErrorMessages, GeometryTypes, RequestType, stateOptions as enumStateOptions } from '@utils/enums';

import {
  generateMapUseTipsAccordionList,
  projectLocationCountries,
  siteDirectionsTooltipContent,
} from '@forms/input-forms/helpers';
import countryRegionData from 'country-region-data/dist/data-umd';
import AddressSuggestInput from '@components/address-suggestion/AddressSuggestInput';
import Tooltip from '@components/tooltip/tooltip';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import RadioButton from '@components/radio-button/RadioButton';
import Checkbox from '@components/checkbox/Checkbox';
import ProjectLocationTabGDB from './ProjectLocationTab.gdb';
import ExistingProjectTab from '../ExistingProjectTab';
import { useGeospatial, useModal, usePlss, useRequest } from '@stores';
import { useExistingProjects, useProjectLocation } from '@api/services/project/useProjects';
import { PLSS_QUERY_KEYS, PlssQueries } from '@api/services/plss';
import { useReverseGeocodeMutation, useAddressCandidatesMutation } from '@hooks';
import { ArcGisQueries, ARC_GIS_QUERY_KEYS } from '@api/services/arcgis';
import { existingProjectTooltipContent } from '@pages/Resources/components/_shared/helper';
import Section from '@components/section/Section';
import { AorQueries } from '@src/api/services/aor';

const ProjectArea = ({ locationType, areaRequired, isReadOnly, handleBlur }) => (
  <Section isSubsection label={`${locationType === 'Project' ? 'Project Area' : 'Area of Violation'}`}>
    <div className='grid-row grid-gap-2'>
      <div className='desktop:grid-col-6 tablet:grid-col margin-bottom-1'>
        <TextInput
          type='number'
          maxLength={17}
          name='projectArea'
          label={`${locationType === 'Project' ? 'Project Area' : 'Area of Violation'} (size)`}
          required={areaRequired === false ? false : true}
          readOnly={isReadOnly}
          onBlur={handleBlur}
        />
      </div>
      <div className='desktop:grid-col-6 tablet:grid-col margin-bottom-1'>
        <SelectInput
          name='unitOfMeasurement'
          label='Unit of Measurement'
          required={areaRequired === false ? false : true}
          readOnly={isReadOnly}
          onBlur={handleBlur}
        >
          <option key={1} value='Square Feet'>
            Square Feet
          </option>
          <option key={2} value='Acres'>
            Acres
          </option>
        </SelectInput>
      </div>
    </div>
  </Section>
);

const districtModalConfig = {
  msg: ErrorMessages.InvalidDistrict,
  status: 'Invalid Geometry Location',
};
const inputType = ['address', 'coordinates'];

const ProjectLocationTab = connect(
  'doClearRequestLocation',
  'doUpdateRequestLocation',
  'doUpdateRequestObject',
  'selectProjectID',
  'selectProjectVersion',
  ({
    doClearRequestLocation,
    doUpdateRequestLocation,
    doUpdateRequestObject,
    areaRequired,
    activeTab,
    fromExistingProject,
    isReadOnly,
    locationType = 'Project',
    projectOutsideUS,
    setProjectOutsideUS,
    requestType,
    projectID,
    projectVersion,
  }) => {
    const {
      formState: { errors },
      watch,
      setValue,
      resetField,
      clearErrors,
    } = useFormContext();
    const { setPlssData, setPlssGeometry, plssGeometry } = usePlss();
    const { mutate: reverseGeocode } = useReverseGeocodeMutation();
    const { mutate: getAddressCandidates } = useAddressCandidatesMutation();
    const {
      projectLocationAddress,
      addressCandidates,
      projectGeometryData,
      setAddressCandidates,
      clearGeospatialStore,
    } = useGeospatial();
    const openSecondaryModal = useModal((state) => state.openSecondaryModal);
    const { resetRequest, setProjectBounds, setProjectGeometry } = useRequest();

    const GDBUploadRef = useRef();

    const [moduleCollapse, setModuleCollapse] = useState(0);
    const [geometry, setGeometry] = useState();
    const [extent, setExtent] = useState();
    const [countyOptions, setCountyOptions] = useState([]);
    const [returnedCounty, setReturnedCounty] = useState();
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedStateName, setSelectedStateName] = useState('');
    const [isExistingProject, setIsExistingProject] = useState(true);

    const errorCount = Object.entries(errors)?.length;
    const inputTypeError = Object.keys(errors).some((error) => error === 'inputType');
    const addressOptionError = Object.keys(errors).some((error) => error === 'addressInputType');

    const projectName = watch('projectName');
    const selectedCountry = watch('country');
    const latitude = watch('latitude');
    const longitude = watch('longitude');
    const address = watch('address');
    const city = watch('city');
    const state = watch('state');
    const zipcode = watch('zipcode');
    const country = watch('country');
    const showMapInput = watch('showMapInput');
    const showGDBInput = watch('showGDBInput');
    const showAddressInput = watch('showAddressInput');
    const addressInputType = watch('addressInputType');
    const hasPreviousFileNumber = watch('hasPreviousFileNumber', 'false');
    const previousFileNumbers = watch('previousFileNumbers', []);
    const hasExistingProject = watch('hasExistingProject');

    const { data: existingProjectData, isFetching } = useExistingProjects(!!hasExistingProject);
    const { data: selectedProjectData } = useProjectLocation({
      projectId: projectID,
      projectVersion: !fromExistingProject ? projectVersion : undefined,
    });

    const currentInputState = useRef({
      Address: showAddressInput,
      Map: showMapInput,
      GDB: showGDBInput,
    });

    const pointGeom = {
      type: 'Point',
      coordinates: [formatCoordFlt(longitude), formatCoordFlt(latitude)],
    };

    const getFirstValidGeometry = (obj) => {
      const extractGeometry = (obj) => {
        if (
          obj.geometry &&
          [
            GeometryTypes.Point,
            GeometryTypes.MultiPoint,
            GeometryTypes.Polygon,
            GeometryTypes.MultiPolygon,
            GeometryTypes.LineString,
            GeometryTypes.MultiLineString,
          ].includes(obj.geometry.type)
        ) {
          return obj.geometry;
        } else if (obj.features) {
          for (let feature of obj.features) {
            const geometry = extractGeometry(feature);
            if (geometry) return geometry;
          }
        }
        return null;
      };

      const geometry = extractGeometry(obj);
      return geometry ? turf.flip(geometry) : null;
    };

    const { data: plssResponseData } = useQuery({
      queryKey: [PLSS_QUERY_KEYS.GET_PLSS, plssGeometry],
      queryFn: () => PlssQueries.getPlss(turf.centerOfMass(plssGeometry).geometry.coordinates.toString()),
      enabled: !!plssGeometry,
    });

    const buildCountyOptions = (counties) => {
      const options = counties.map((county) => (
        <option key={county} value={county}>
          {county}
        </option>
      ));
      setCountyOptions(options);
    };

    useQuery({
      queryKey: [ARC_GIS_QUERY_KEYS.GET_COUNTIES_BY_STATE, selectedStateName],
      queryFn: async () => {
        const counties = await ArcGisQueries.getCountyListByState(selectedStateName);
        buildCountyOptions(counties);
        return counties;
      },
      enabled: !!selectedStateName,
    });

    useEffect(() => {
      if (plssResponseData?.features?.length > 0) {
        const plssData = plssResponseData?.features?.[0]?.attributes;
        const { FRSTDIVNO, TWNSHPNO, RANGENO, RANGEDIR, TWNSHPDIR } = plssData;
        setPlssData({
          FRSTDIVNO: FRSTDIVNO,
          TWNSHPNO: TWNSHPNO,
          RANGENO: RANGENO,
          RANGEDIR: RANGEDIR,
          TWNSHPDIR: TWNSHPDIR,
        });
        doUpdateRequestLocation({
          plssSection: FRSTDIVNO,
          plssTownship: TWNSHPNO,
          plssRange: RANGENO,
          plssRangeDir: RANGEDIR,
          plssTownshipDir: TWNSHPDIR,
        });
      } else {
        setPlssData(null);
        doUpdateRequestLocation({
          plssSection: null,
          plssTownship: null,
          plssRange: null,
          plssRangeDir: null,
          plssTownshipDir: null,
        });
      }
    }, [plssResponseData, setPlssData, doUpdateRequestLocation]);

    const resetFields = () => {
      setExtent();
      setGeometry();
      setPlssData(null);
      setPlssGeometry(null);
      clearGeospatialStore();
      doUpdateRequestLocation({});
      setAddressCandidates([]);
      doClearRequestLocation();
      resetRequest();
      resetField('address');
      resetField('addressTwo');
      resetField('city');
      resetField('state');
      resetField('zipcode');
      resetField('county');
      resetField('projectArea');
      resetField('unitOfMeasurement');
      setValue('latitude', undefined);
      setValue('longitude', undefined);
      clearErrors('inputType');
    };

    const handleClearFileInput = () => {
      GDBUploadRef?.current && GDBUploadRef.current.clearFiles();
      clearGeospatialStore();
      setExtent();
      setGeometry();
    };

    const handleExistingProject = (value) => {
      setValue('hasExistingProject', value, { shouldValidate: true });

      setIsExistingProject(value);

      if (!value) {
        setValue('existingProjectId', null, { shouldValidate: true });
      }
    };

    const handlePreviousProject = (selected) => {
      setValue('existingProjectId', selected || [], { shouldValidate: true });
    };

    const handleSelectChange = (e) => {
      const results = e?.map((num) => isValidORMNumber(num.value));

      if (!results.every(Boolean)) {
        setValue('previousFileNumbers', e?.length > 1 ? previousFileNumbers : []);
        doUpdateRequestObject({
          ormDANumber: e?.length > 1 ? previousFileNumbers : null,
          hasPreviousFileNumber: 'false',
        });
        alert(
          'An Invalid ORM file number was detected. ORM file numbers should conform to the following: \n\n First 3 characters should be one of the following: \n\n MVM, MVN, MVS, MVK, MVR, MVP, NAB, NAN, NAO, NAP, NAE, NWP, NWW, NWK, NWO, NWS, LRH, LRL, LRN, LRP, LRB, LRC, LRE, POH, POA, SAJ, SAC, SAM, SAS, SAW, SPL, SPK, SPN, SPA, SWF, SWG, SWL, SWT, SAA \n\n 4th character is a dash "-" \n\n Characters 5-8 should be a 4 digit number between 1900-the current year \n\n 9th character is a dash "-" \n\n Remaining characters could be 5 or 6 digits and may only be numbers.\n\n Optional 5 character suffix is also supported. Example: MVM-2023-12345 or MVS-2020-123456-MT123'
        );
      }
    };

    const handlePreviousFileNumberChange = (e) => {
      if (e?.target?.value === 'false') {
        setValue('previousFileNumbers', [], { shouldValidate: true });
      }
    };

    const handleBlur = async (e) => {
      const didAddressFieldChange = (field) => ['address', 'city', 'state', 'zipcode', 'country'].includes(field);
      const targetName = e?.target?.name;
      const targetValue = e?.target?.value;

      if (targetName === 'latitude' || targetName === 'longitude') {
        if (latRegex.test(latitude) && lngRegex.test(longitude)) {
          const formattedLatFlt = formatCoordFlt(latitude);
          const formattedLngFlt = formatCoordFlt(longitude);
          const pointGeometry = { type: 'Point', coordinates: [formattedLatFlt, formattedLngFlt] };
          const reversePointGeometry = { type: 'Point', coordinates: [formattedLngFlt, formattedLatFlt] };

          const geoJsonLayer = L.geoJson(reversePointGeometry);
          const extent = geoJsonLayer.getBounds();
          const bounds = [
            [extent._northEast.lat, extent._northEast.lng],
            [extent._southWest.lat, extent._southWest.lng],
          ];

          if (reversePointGeometry?.coordinates?.length > 0) {
            setGeometry(reversePointGeometry);
            setPlssGeometry(reversePointGeometry);
          }

          reverseGeocode({ location: `${longitude}, ${latitude}`, local: false });
          pointGeometry && setProjectGeometry(pointGeometry);

          if (reversePointGeometry) {
            const isValidDistrict = await AorQueries.isGeometryInValidDistrict(reversePointGeometry);
            if (isValidDistrict === false) openSecondaryModal(statusModal, districtModalConfig);
          }
          setProjectBounds(bounds);
          setExtent(bounds);
        }
        //Add Marker to Map
        if (!geometry || geometry.type === 'Point') {
          doUpdateRequestLocation({ geometry: pointGeom });
          setProjectGeometry(pointGeom);
          if (didAddressFieldChange(targetName)) {
            const isValidDistrict = await AorQueries.isGeometryInValidDistrict(pointGeom);
            if (isValidDistrict === false) openSecondaryModal(statusModal, districtModalConfig);
          }
        }
        doUpdateRequestLocation({ [targetName]: formatCoordFlt(targetValue) });

        return;
      }

      if (address && city && state && zipcode && country && didAddressFieldChange(targetName)) {
        const addressString = `${address}, ${city}, ${state} ${zipcode}, ${country}`;
        getAddressCandidates({ address: addressString });
      }

      if (targetName && targetValue) {
        doUpdateRequestLocation({ [targetName]: targetValue });
      }
    };

    const handleCheckboxChange = () => {
      setProjectOutsideUS((prev) => !prev);
    };

    const handleInputChange = (e) => {
      //Used to account for browser autofill
      if (e?.target?.name) {
        doUpdateRequestLocation({ [e.target.name]: e?.target?.value });
      }
    };

    const handleAddressOnChange = (e) => {
      setValue('addressInputType', e?.target?.value, { shouldValidate: true });
      scrollToElement('second_scroll');
      resetFields();
    };

    const handleStateOnChange = async (e) => {
      resetField('county', '');
      doUpdateRequestLocation({ county: '' });
      setReturnedCounty(undefined);

      const state = e?.target?.selectedOptions[0]?.label;
      setSelectedStateName(state);

      if (!state) {
        setCountyOptions([]);
      }
    };

    const populateAddressFields = async (address, isProjectAddress) => {
      const fields = address?.candidates?.[0]?.attributes;
      const addressField = document.getElementById('address');
      const addressFieldFocused = document.activeElement === addressField;

      if (fields) {
        const region = fields.Region;
        setSelectedStateName(region);

        if (addressFieldFocused) {
          addressField.blur();
        }

        setValue('address', fields.StAddr, { shouldValidate: true });
        setValue('addressTwo', fields.SubAddr, { shouldValidate: true });
        setValue('city', fields.City, { shouldValidate: true });
        setValue('state', fields.RegionAbbr, { shouldValidate: true });
        setValue('zipcode', fields.Postal, { shouldValidate: true });
        setReturnedCounty(fields.Subregion);
        setValue('country', mapCountryAlpha3toAlpha2(fields.Country), { shouldValidate: true });
        if (isProjectAddress) {
          setValue('latitude', formatCoordStr(fields.Y), { shouldValidate: true });
          setValue('longitude', formatCoordStr(fields.X), { shouldValidate: true });
          doUpdateRequestLocation({
            address: fields?.StAddr,
            addressTwo: fields?.SubAddr,
            city: fields?.City,
            state: fields?.RegionAbbr,
            zipcode: fields?.Postal,
            county: fields?.Subregion,
            country: fields?.Country !== 'USA' ? fields?.Country : 'US',
            latitude: formatCoordFlt(fields.Y),
            longitude: formatCoordFlt(fields.X),
          });
        } else {
          setAddressCandidates([]);
        }

        if (addressFieldFocused) {
          addressField.focus();
        }
      }
    };

    const toggleInputType = (type) => {
      const selectedType = Object.keys(currentInputState.current).find(
        (key) => currentInputState.current[key] === true
      );

      if (selectedType === type) {
        return;
      }
      if (!currentInputState.current[type]) {
        resetFields();
        scrollToElement('first_scroll');
      }

      switch (type) {
        case 'Address':
          setValue('showAddressInput', true);
          setValue('showMapInput', false);
          setValue('showGDBInput', false);
          setModuleCollapse(moduleCollapse !== 1 ? 1 : 0);
          errorCount > 0 && clearErrors();
          break;
        case 'Map':
          setValue('showAddressInput', false);
          setValue('showMapInput', true);
          setValue('showGDBInput', false);
          setModuleCollapse(moduleCollapse !== 2 ? 2 : 0);
          errorCount > 0 && clearErrors();
          break;
        case 'GDB':
          setValue('showAddressInput', false);
          setValue('showMapInput', false);
          setValue('showGDBInput', true);
          setModuleCollapse(moduleCollapse !== 3 ? 3 : 0);
          errorCount > 0 && clearErrors();
          break;
        default:
          setModuleCollapse(0);
          resetFields();
          break;
      }

      Object.keys(currentInputState.current).forEach((key) => {
        currentInputState.current[key] = key === type;
      });
    };

    const regionHelper = useCallback((country) => {
      const selectedCountryData = countryRegionData?.find(
        (regionCountry) => regionCountry.countryShortCode === country
      );

      let regions = [];
      if (selectedCountryData) {
        regions = selectedCountryData.regions.map((region) => ({
          value: region.shortCode ?? region.name,
          text: region.name,
        }));
      }

      regions.push({ value: 'Other', text: 'Other' });
      regions.push({ value: 'None', text: 'None' });

      return regions;
    }, []);

    const handleCountryChange = useCallback(
      (selectedCountry) => {
        const regions = regionHelper(selectedCountry);
        setStateOptions(regions);
        setValue('city', '');
        setValue('state', '');
        setValue('zipcode', '');
        setValue('county', '');
        setCountyOptions([]);
      },
      [regionHelper, setValue]
    );

    const refreshMapWithGDBData = useCallback(
      (type, coordinates) => {
        switch (type) {
          case GeometryTypes.Point:
            reverseGeocode({
              location: `${formatCoordStr(coordinates[0])}, ${formatCoordStr(coordinates[1])}`,
              local: false,
            });
            setValue('latitude', formatCoordStr(coordinates[1]), { shouldValidate: true });
            setValue('longitude', formatCoordStr(coordinates[0]), { shouldValidate: true });
            doUpdateRequestLocation({
              latitude: formatCoordFlt(coordinates[1]),
              longitude: formatCoordFlt(coordinates[0]),
            });
            break;

          case GeometryTypes.MultiPoint:
            if (coordinates.length > 0) {
              const firstPoint = coordinates[0];
              reverseGeocode({
                location: `${formatCoordStr(firstPoint[0])}, ${formatCoordStr(firstPoint[1])}`,
                local: false,
              });
              setValue('latitude', formatCoordStr(firstPoint[1]), { shouldValidate: true });
              setValue('longitude', formatCoordStr(firstPoint[0]), { shouldValidate: true });
              doUpdateRequestLocation({
                latitude: formatCoordFlt(firstPoint[1]),
                longitude: formatCoordFlt(firstPoint[0]),
              });
            }
            break;

          case GeometryTypes.Polygon:
          case GeometryTypes.MultiPolygon:
            const firstPolygon = type === GeometryTypes.MultiPolygon ? coordinates[0] : coordinates;
            const turfPolygon = turf.polygon(firstPolygon);
            const polygonCentroid = turf.centerOfMass(turfPolygon).geometry.coordinates;
            reverseGeocode({
              location: `${formatCoordStr(polygonCentroid[0])}, ${formatCoordStr(polygonCentroid[1])}`,
              local: false,
            });
            setValue('latitude', formatCoordStr(polygonCentroid[1]), { shouldValidate: true });
            setValue('longitude', formatCoordStr(polygonCentroid[0]), { shouldValidate: true });
            doUpdateRequestLocation({
              latitude: formatCoordFlt(polygonCentroid[1]),
              longitude: formatCoordFlt(polygonCentroid[0]),
            });

            // Calculate Project Area (Acres)
            const area = turf.area(turfPolygon);
            const formattedArea = area / 4046.856422;
            const roundedArea = formattedArea.toFixed(6);
            setValue('projectArea', roundedArea, { shouldValidate: true });
            setValue('unitOfMeasurement', 'Acres', { shouldValidate: true });
            doUpdateRequestLocation({ projectArea: Number(roundedArea), unitOfMeasurement: 'Acres' });
            break;

          case GeometryTypes.LineString:
          case GeometryTypes.MultiLineString: {
            const firstLine = coordinates[0];

            const line = turf.lineString(firstLine);
            const lineLength = turf.length(line, { units: 'kilometers' });
            const lineCentroid = turf.along(line, lineLength / 2, { units: 'kilometers' });

            const [lng, lat] = lineCentroid.geometry.coordinates;

            reverseGeocode({
              location: `${formatCoordStr(lat)}, ${formatCoordStr(lng)}`,
              local: false,
            });

            setValue('latitude', formatCoordStr(lat), { shouldValidate: true });
            setValue('longitude', formatCoordStr(lng), { shouldValidate: true });

            doUpdateRequestLocation({
              latitude: formatCoordFlt(lat),
              longitude: formatCoordFlt(lng),
            });

            break;
          }

          default:
            break;
        }
      },
      [reverseGeocode, doUpdateRequestLocation, setValue]
    );

    useEffect(() => {
      if (projectLocationAddress) {
        const updateProjectLocation = async () => {
          let resolvedState = projectLocationAddress.RegionAbbr;
          if (!resolvedState && latitude != null && longitude != null) {
            const centroidGeometry = {
              type: GeometryTypes.Point,
              coordinates: [formatCoordFlt(longitude), formatCoordFlt(latitude)],
            };
            const districtLocations = await AorQueries.getDistrictLocationFromGeometry(centroidGeometry);
            resolvedState = districtLocations?.[0]?.stateCode ?? null;
          }

          setValue('address', projectLocationAddress.Address, { shouldValidate: true });
          setValue('addressTwo', projectLocationAddress.SubAddr, { shouldValidate: true });
          setValue('city', projectLocationAddress.City, { shouldValidate: true });
          if (resolvedState) {
            setValue('state', resolvedState, { shouldValidate: true });
          }
          setValue('zipcode', projectLocationAddress.Postal, { shouldValidate: true });
          setValue('county', projectLocationAddress.Subregion, { shouldValidate: true });
          setValue('country', mapCountryAlpha3toAlpha2(projectLocationAddress.CountryCode), { shouldValidate: true });
          // ESRI API RETURNS 'USA' and 'United States', dropdown expects 'US' or 'United States of America'
          doUpdateRequestLocation({
            address: projectLocationAddress.Address,
            addressTwo: projectLocationAddress.SubAddr,
            city: projectLocationAddress.City,
            ...(resolvedState && { state: resolvedState }),
            zipcode: projectLocationAddress.Postal,
            county: projectLocationAddress.Subregion,
            country: projectLocationAddress.Country !== 'USA' ? projectLocationAddress.Country : 'US',
          });
        };
        updateProjectLocation();
      }
    }, [projectLocationAddress, latitude, longitude, setValue, doUpdateRequestLocation]);

    useEffect(() => {
      if (projectGeometryData) {
        const projectGeometry = getFirstValidGeometry(projectGeometryData);
        if (projectGeometry) {
          setGeometry(projectGeometry);
          setPlssGeometry(projectGeometry);
          doUpdateRequestLocation({ geometry: projectGeometry });
          setProjectGeometry(projectGeometry);

          const coordinates = projectGeometry?.coordinates;
          const type = projectGeometry?.type;

          refreshMapWithGDBData(type, coordinates);
          const geoJsonLayer = L.geoJson(projectGeometry);
          const extent = geoJsonLayer.getBounds();
          const bounds = [
            [extent._northEast.lat, extent._northEast.lng],
            [extent._southWest.lat, extent._southWest.lng],
          ];
          setProjectBounds(bounds);
          setExtent(bounds);
        }
      }
    }, [
      doUpdateRequestLocation,
      projectGeometryData,
      refreshMapWithGDBData,
      setPlssGeometry,
      setProjectBounds,
      setProjectGeometry,
    ]);

    useEffect(() => {
      const candidates = addressCandidates?.[0];
      const location = candidates?.location;
      const extent = candidates?.extent;

      if (location && addressInputType !== 'coordinates') {
        //IMPORTANT: location coords are returned from API in (x: long, y: lat) format
        doUpdateRequestLocation({ latitude: formatCoordFlt(location?.y), longitude: formatCoordFlt(location?.x) });
        setValue('latitude', formatCoordStr(formatCoordStr(location?.y)), { shouldValidate: true });
        setValue('longitude', formatCoordStr(formatCoordStr(location?.x)), { shouldValidate: true });

        const geometry = { type: 'Point', coordinates: [formatCoordFlt(location?.x), formatCoordFlt(location?.y)] };
        setGeometry(geometry);
        doUpdateRequestLocation({ geometry });

        AorQueries.isGeometryInValidDistrict(geometry);
        doUpdateRequestLocation({ geometry: geometry });
        setProjectGeometry(geometry);

        if (Math.sign(location?.y) === -1 || Math.sign(location?.x) === 1) {
          setProjectOutsideUS(true);
        } else {
          setProjectOutsideUS(false);
        }
      }

      if (extent) {
        const boundary = [
          [extent.ymin, extent.xmin],
          [extent.ymax, extent.xmax],
        ];
        setProjectBounds(boundary);
        setExtent(boundary);
      }
    }, [
      addressCandidates,
      addressInputType,
      doUpdateRequestLocation,
      setProjectBounds,
      setProjectGeometry,
      setProjectOutsideUS,
      setValue,
    ]);

    useEffect(() => {
      if (!projectLocationCountries.some((country) => country.value === selectedCountry) && selectedCountry !== '') {
        setValue('country', '');
      }
    }, [selectedCountry, setValue]);

    useEffect(() => {
      returnedCounty && setValue('county', returnedCounty, { shouldValidate: true });
    }, [returnedCounty, countyOptions, setValue]);

    useEffect(() => {
      const regions = regionHelper(country);
      setStateOptions(regions);
    }, [country, regionHelper, setStateOptions]);

    const projOptions = useMemo(() => {
      if (isFetching || !existingProjectData) return [];

      const buildLabel = (projectName, daNumbers) => {
        if (!daNumbers || daNumbers.length === 0) return projectName;
        return `${projectName} (${daNumbers.join(', ')})`;
      };

      const existingOptions = {};
      const options = existingProjectData
        .reduce((acc, project) => {
          if (existingOptions[project.projectID]) {
            return acc;
          }

          existingOptions[project.projectID] = true;

          acc.push({
            label: buildLabel(project.projectName, project.daNumbers || []),
            value: project.projectID,
            projectVersion: project.projectVersion,
            daNumbers: project.daNumbers || [],
          });

          return acc;
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label));

      return options.length > 0 ? options : [{ label: 'No Existing Project Found', value: '', isDisabled: true }];
    }, [existingProjectData, isFetching]);

    const showUsStates = country === 'US' || country === null || country === '';
    const statesLabel = showUsStates ? 'State' : 'Region';
    const stateOptionData = showUsStates ? enumStateOptions : stateOptions;

    return (
      <div className='project-location-tab padding-left-2 padding-right-2'>
        {errors && <ErrorSummary errors={errors} modalID='projectLocationTab' type='modal' />}

        <div>
          <div className='text-italic'>
            A red asterisk (<span className='required' />) indicates a required field.
          </div>
          {requestType !== RequestType.ViolationComplaint && (
            <div>
              <YesNoInput
                name='hasExistingProject'
                label='Would you like to start a new request based on an existing RRS project?'
                required
                onChange={handleExistingProject}
              />
              {hasExistingProject && (
                <>
                  <MultiSelectInput
                    name='existingProjectId'
                    label='Choose an Existing Project'
                    required
                    tooltip={existingProjectTooltipContent}
                    options={projOptions}
                    handleChange={handlePreviousProject}
                    menuPosition='fixed'
                    onBlur={handleBlur}
                    filterKeys={['daNumber']}
                    isLoading={isFetching}
                    closeMenuOnSelect
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        zIndex: '9999',
                        minWidth: '200px',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        position: 'relative',
                        width: '30vw',
                      }),
                      menuList: (provided) => ({
                        ...provided,
                        maxHeight: '35vh',
                      }),
                    }}
                  />
                  <Alert noIcon slim className='callout margin-bottom-1 text-italic'>
                    When you select an existing project, its location and general project information will be reused and
                    cannot be edited.
                  </Alert>
                </>
              )}
            </div>
          )}
          {hasExistingProject && selectedProjectData && (
            <div className='margin-top-2'>
              <ExistingProjectTab projectData={selectedProjectData} />
            </div>
          )}
        </div>

        <div id='projectLocationTab'>
          {(requestType === RequestType.ViolationComplaint || (!isExistingProject && locationType === 'Project')) && (
            <>
              {requestType !== RequestType.ViolationComplaint && (
                <Section label='Project Information'>
                  <div>
                    <TextInput
                      name='projectName'
                      label='Project Name'
                      type='text'
                      required
                      readOnly={isReadOnly}
                      onBlur={handleBlur}
                    />
                  </div>
                </Section>
              )}
              {(!isExistingProject || requestType !== RequestType.ViolationComplaint) && (
                <Section>
                  <div>
                    <SelectInput
                      className='width-50'
                      fullWidth={false}
                      name='hasPreviousFileNumber'
                      label='Has the USACE previously issued a file number for any part of the project area?'
                      required
                      readOnly={isReadOnly}
                      onChange={handlePreviousFileNumberChange}
                      onBlur={handleBlur}
                    >
                      <option key='2' value='true'>
                        Yes
                      </option>
                      <option key='3' value='false'>
                        No
                      </option>
                    </SelectInput>
                  </div>

                  {hasPreviousFileNumber === 'true' && (
                    <div className='row margin-top-1 margin-bottom-2'>
                      <CreatableSelectInput
                        name='previousFileNumbers'
                        label='Previous File Number(s)'
                        tooltip='Enter the file number and press "ENTER" or click on "Create ..."'
                        placeholder='e.g. MVS-2023-12345, MVM-2020-1234 ...'
                        required
                        multi
                        handleChange={handleSelectChange}
                        readOnly={isReadOnly}
                      />
                    </div>
                  )}
                </Section>
              )}
              <Section label={`Project Location`}>
                <div>
                  {locationType !== 'Site' ? (
                    <Alert type='warning' headingLevel='h4' heading='IMPORTANT' slim noIcon>
                      It is critical to accurately input the location during the initial creation of the{' '}
                      {locationType.toLowerCase()}. The {locationType.toLowerCase()}'s location determines the range of
                      available requests within that specific geographical area. If an error is made in entering the{' '}
                      {locationType.toLowerCase()} location, it will be necessary to delete the request and initiate a
                      new one for the {locationType.toLowerCase()}.
                    </Alert>
                  ) : (
                    <div>{`Please provide the ${locationType.toLowerCase()} location of the potential violation being reported.`}</div>
                  )}
                </div>
                <div className='margin-top-2 align-content-center' id='inputType'>
                  <h5 className='text-center  margin-top-1'>
                    How would you like to provide the {locationType} Location?{' '}
                  </h5>
                  <div className='margin-top-2'>
                    <div className='grid-row grid-gap-2'>
                      <div className='desktop:grid-col-4 tablet:grid-col margin-bottom-1'>
                        <ModuleButton
                          title='Type in Address'
                          imageAlt='Type in Address'
                          text='Provide us with a physical address or latitude and longitude coordinates'
                          subtext={`Recommended for smaller ${locationType.toLowerCase()}s.`}
                          icon={mdiKeyboard}
                          onClick={() => toggleInputType('Address')}
                          isModalButton
                          isError={inputTypeError}
                          selected={showAddressInput}
                          hideDetails={moduleCollapse !== 0}
                        />
                      </div>
                      <div className='desktop:grid-col-4 tablet:grid-col margin-bottom-1'>
                        <ModuleButton
                          title='Use a Map'
                          imageAlt='Use a Map'
                          text='Provide us with an address by placing a point, line or polygon on a map.'
                          subtext={`Recommended for small and medium  ${locationType.toLowerCase()}s.`}
                          icon={mdiMapSearchOutline}
                          onClick={() => toggleInputType('Map')}
                          isModalButton
                          isError={inputTypeError}
                          selected={showMapInput}
                          hideDetails={moduleCollapse !== 0}
                        />
                      </div>
                      <div className='desktop:grid-col-4 tablet:grid-col margin-bottom-1'>
                        <ModuleButton
                          title='Upload a Geodatabase'
                          imageAlt='Upload a Geodatabase'
                          text={`Provide us with ${locationType.toLowerCase()} boundaries by uploading a geodatabase file.`}
                          subtext={`Recommended for larger ${locationType.toLowerCase()}s.`}
                          icon={mdiDatabaseMarker}
                          onClick={() => toggleInputType('GDB')}
                          isModalButton
                          isError={inputTypeError}
                          selected={showGDBInput}
                          hideDetails={moduleCollapse !== 0}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {showAddressInput && (
                  <>
                    <div className='justify-content-center' id='first_scroll'>
                      <div>
                        <fieldset
                          className={`margin-left-0 margin-right-0 radio-button-group margin-top-2 ${addressOptionError ? 'radio-fieldset-invalid' : ''}`}
                          id='addressInputType'
                        >
                          <legend htmlFor='addressCoordinates'>I would like to enter ...</legend>
                          <RadioButton
                            className='padding-1'
                            id='radio-address'
                            label={
                              <>
                                The <span className='text-bold'>physical address</span> for the{' '}
                                {locationType.toLocaleLowerCase()} location
                              </>
                            }
                            name='addressCoordinates'
                            onChange={handleAddressOnChange}
                            value='address'
                            checked={addressInputType === 'address'}
                          />
                          <RadioButton
                            className='padding-1'
                            id='radio-coordinates'
                            label={
                              <>
                                The <span className='text-bold'>coordinates</span> for the{' '}
                                {locationType.toLocaleLowerCase()} location
                              </>
                            }
                            name='addressCoordinates'
                            onChange={handleAddressOnChange}
                            value='coordinates'
                            checked={addressInputType === 'coordinates'}
                          />
                        </fieldset>
                      </div>
                    </div>
                    {inputType?.includes(addressInputType) && (
                      <div className='grid-row grid-gap-2 margin-top-2'>
                        <div className='desktop:grid-col-6 tablet:grid-col-12 margin-bottom-1'>
                          <h4 className='border-bottom width-full padding-bottom-1 margin-top-3' id='second_scroll'>
                            {locationType} {addressInputType === 'address' ? 'Address' : 'Coordinates'}
                          </h4>
                          {addressInputType === 'address' && (
                            <>
                              <div>
                                <AddressSuggestInput
                                  label='Address'
                                  name='address'
                                  required
                                  isProjectAddress
                                  onBlur={handleBlur}
                                  onChange={handleInputChange}
                                  populateAddressFields={populateAddressFields}
                                />
                              </div>
                              <div>
                                <TextInput
                                  label='Address Line 2'
                                  name='addressTwo'
                                  type='text'
                                  onBlur={handleBlur}
                                  onChange={handleInputChange}
                                />
                              </div>
                              <div>
                                <SelectInput
                                  name='country'
                                  label='Country'
                                  onBlur={handleBlur}
                                  onChange={(e) => handleCountryChange(e.target.value)}
                                  required
                                >
                                  {projectLocationCountries.map((item, i) => (
                                    <option key={i + 2} value={item.value}>
                                      {item.text}
                                    </option>
                                  ))}
                                </SelectInput>
                              </div>
                              <div className='grid-row grid-gap-2'>
                                <div className='desktop:grid-col-4  tablet:grid-col margin-bottom-1'>
                                  <TextInput
                                    label='City'
                                    name='city'
                                    type='text'
                                    maxLength={26}
                                    onBlur={handleBlur}
                                    onChange={handleInputChange}
                                    required
                                  />
                                </div>
                                <div className='desktop:grid-col-4  tablet:grid-col margin-bottom-1'>
                                  <SelectInput
                                    name='state'
                                    label={statesLabel}
                                    onBlur={handleBlur}
                                    onChange={handleStateOnChange}
                                    required
                                  >
                                    {stateOptionData.map((item, i) => (
                                      <option key={i + 2} value={showUsStates ? item.abbr : item.value}>
                                        {item.text}
                                      </option>
                                    ))}
                                  </SelectInput>
                                </div>
                                <div className='desktop:grid-col-4  tablet:grid-col margin-bottom-1'>
                                  <TextInput
                                    type='tel'
                                    label={
                                      country === 'US' || country === null || country === ''
                                        ? 'Zip Code'
                                        : 'Postal code'
                                    }
                                    name='zipcode'
                                    maxLength={5}
                                    onBlur={handleBlur}
                                    onChange={handleInputChange}
                                    required
                                  />
                                </div>
                              </div>
                              <div>
                                <SelectInput
                                  name='county'
                                  label='County'
                                  readOnly={isReadOnly}
                                  onChange={handleInputChange}
                                  onBlur={handleBlur}
                                >
                                  {...countyOptions}
                                </SelectInput>
                              </div>
                            </>
                          )}
                          {addressInputType === 'coordinates' && (
                            <>
                              <div className='text-italic margin-top-1'>Coordinates must be in Decimal Degrees</div>
                              <fieldset className='margin-top-2' id='projectOutside'>
                                <Checkbox
                                  id='address-project-outside'
                                  name='projectOutside'
                                  label={
                                    <>
                                      My {locationType.toLowerCase()} is outside of the continental U.S.
                                      <Tooltip
                                        content={`Select this option for ${locationType.toLowerCase()}s in Guam or other U.S. Territories outside of the continental U.S.`}
                                      />
                                    </>
                                  }
                                  onChange={handleCheckboxChange}
                                  value='projectOutsideUS'
                                  checked={projectOutsideUS}
                                />
                              </fieldset>
                              <div className='grid-row grid-gap-2'>
                                <div className='grid-col-6'>
                                  <TextInput
                                    maxLength={12}
                                    name='latitude'
                                    label='Latitude'
                                    readOnly={isReadOnly}
                                    onBlur={handleBlur}
                                    required
                                  />
                                </div>
                                <div className='grid-col-6'>
                                  <TextInput
                                    maxLength={12}
                                    name='longitude'
                                    label='Longitude'
                                    readOnly={isReadOnly}
                                    onBlur={handleBlur}
                                    required
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          <ProjectArea
                            areaRequired={areaRequired}
                            handleBlur={handleBlur}
                            isReadOnly={isReadOnly}
                            locationType={locationType}
                          />
                        </div>
                        <div className='desktop:grid-col-6 tablet:grid-col-12 margin-bottom-1'>
                          <h4 className='border-bottom width-full padding-bottom-1 padding-top-1 margin-top-3'>
                            {locationType} Map
                          </h4>
                          {activeTab === 0 && (
                            <ReadOnlyMap extent={extent} geometry={geometry} geometryName={projectName} height='50vh' />
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {showMapInput && (
                  <>
                    <div>
                      <div>
                        <Accordion
                          bordered
                          items={generateMapUseTipsAccordionList('resource')}
                          className='margin-top-2 margin-bottom-1'
                        />
                      </div>
                    </div>
                    {activeTab === 0 && (
                      <div id='mapScroll'>
                        <Map disableFileUpload={true} setProjectOutsideUS={setProjectOutsideUS} />
                      </div>
                    )}
                    <div>
                      <fieldset className='margin-top-2' id='projectOutside'>
                        <Checkbox
                          id='map-project-outside'
                          name='projectOutside'
                          label={
                            <>
                              My {locationType.toLowerCase()} is outside of the continental U.S.
                              <Tooltip
                                content={`Select this option for ${locationType.toLowerCase()}s in Guam or other U.S. Territories outside of the continental U.S.`}
                              />
                            </>
                          }
                          onChange={handleCheckboxChange}
                          value='projectOutsideUS'
                          checked={projectOutsideUS}
                        />
                      </fieldset>
                    </div>
                    <div className='grid-row grid-gap-2'>
                      <div className='desktop:grid-col-6 tablet:grid-col-12 margin-bottom-1'>
                        <TextInput
                          maxLength={12}
                          name='latitude'
                          label='Latitude'
                          readOnly
                          onBlur={handleBlur}
                          required
                        />
                      </div>
                      <div className='desktop:grid-col-6 tablet:grid-col-12 margin-bottom-1'>
                        <TextInput
                          maxLength={12}
                          name='longitude'
                          label='Longitude'
                          readOnly
                          onBlur={handleBlur}
                          required
                        />
                      </div>
                    </div>
                    <ProjectArea
                      areaRequired={areaRequired}
                      handleBlur={handleBlur}
                      isReadOnly={isReadOnly}
                      locationType={locationType}
                    />
                  </>
                )}
                {showGDBInput && (
                  <>
                    <ProjectLocationTabGDB
                      activeTab={activeTab}
                      deleteFileCallback={handleClearFileInput}
                      extent={extent}
                      geometry={geometry}
                      locationType={locationType}
                      resetFieldsCallback={resetFields}
                    />
                    <ProjectArea
                      areaRequired={areaRequired}
                      handleBlur={handleBlur}
                      isReadOnly={isReadOnly}
                      locationType={locationType}
                    />
                  </>
                )}
                {(inputType?.includes(addressInputType) || showMapInput || showGDBInput) && (
                  <Section label={`Additional ${locationType} Information`}>
                    <div className='grid-row'>
                      <div className='desktop:grid-col-8 tablet:grid-col margin-bottom-1'>
                        <TextInput
                          name='parcelNumber'
                          label='Parcel Number'
                          type='text'
                          readOnly={isReadOnly}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div className='grid-row'>
                      <div className='desktop:grid-col-8 tablet:grid-col margin-bottom-1'>
                        <TextInput
                          name='nearestWaterbody'
                          label='Nearest Waterbody'
                          type='text'
                          readOnly={isReadOnly}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div>
                      <TextAreaInput
                        name='siteDirections'
                        label={`Directions to Project Location`}
                        readOnly={isReadOnly}
                        onBlur={handleBlur}
                        tooltip={siteDirectionsTooltipContent}
                      />
                    </div>
                  </Section>
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    );
  }
);

export default ProjectLocationTab;
